/**
 * Aggregated types module for unplugin-auto-import
 */

export type ComponentType = {
  id: number;
  name: string;
  description: string;
  cost_unit_calculation_method: string;
  created: string;
  last_updated: string;
};

export type ComponentTypeCreateUpdate = {
  id?: number;
  name?: string;
  description?: string;
  cost_unit_calculation_method?: string;
};

export type CostPool = {
  id: number;
  name: string;
  description: string;
  type: string;
  unit_type?: string;
  unit_description?: string;
  unit_weight_method?: string;
  estimated_capacity?: number;
  max_capacity?: number;
  cost_center_identifier: string;
  percentage_of_cost_center?: string | number;
  created: string;
  last_updated: string;
};

export type CostPoolCreateUpdate = {
  id?: number;
  name: string;
  description: string;
  type: string;
  unit_type?: string;
  unit_description?: string;
  unit_weight_method?: string;
  estimated_capacity?: number;
  max_capacity?: number;
  cost_center_identifier: string;
  percentage_of_cost_center?: number | string;
};

export type Feature = {
  id: number;
  name: string;
  description: string;
};
export type FeatureCreateUpdate = {
  id?: number;
  name: string;
  description: string;
};

export type MappedFeatureLink = {
  id: number;
  name: string;
  customer_visibility: boolean;
};

export type FeatureLink = {
  customer_visibility: boolean;
  product_feature_id: number;
};

export type LaborComponent = {
  id: number;
  name: string;
  description: string;
  estimated_labor: string;
  cost_pool_id: number;
  created?: string;
  last_updated?: string;
};

export type LaborComponentCreateUpdate = {
  id?: number;
  name: string;
  description: string;
  estimated_labor: number | string;
  cost_pool_id: number;
};

export type ServiceLevelObjectivePenalty = {
  id: number;
  slo_id: number;
  name: string;
  description: string;
  percentage: number;
  service_credit: number;
  created: string;
  last_updated: string;
};
export type ServiceLevelObjectivePenaltyCreateUpdate = {
  id?: number;
  slo_id: number;
  name: string;
  description: string;
  percentage: number;
  service_credit: number;
};

export type ProductCategory = {
  id: number;
  name: string;
  sku: string;
  sku_part: string;
  created: string;
  last_updated: string;
};

export type ProductCategoryCreateUpdate = {
  id?: number;
  name?: string;
  sku_part?: string;
};

export type ProductComponent = {
  id: number;
  name: string;
  description: string;
  component_type_id: number;
  cost_pool_id: number;
  direct_cost_identifier: string;
  direct_cost_weight: number;
  direct_cost_weight_description: string;
  estimated_cost_units: number;
  estimated_cost_description: string;
  personal_data_stored: boolean;
  oem_can_access_data: boolean;
  reseller_can_access_data: boolean;
  direct_product_offering_id?: number;
  created: string;
  last_updated: string;
};

export type ProductComponentCreateUpdate = {
  id?: number;
  name: string;
  description: string;
  component_type_id: number;
  cost_pool_id: number;
  direct_cost_identifier?: string;
  direct_cost_weight?: number;
  direct_cost_weight_description?: string;
  estimated_cost_units?: number;
  estimated_cost_description?: string;
  personal_data_stored: boolean;
  oem_can_access_data: boolean;
  reseller_can_access_data: boolean;
  direct_product_offering_id?: number;
};

export type ProductGroup = {
  id: number;
  subcategory_id: number;
  name: string;
  description: string;
  sku: string;
  sku_part: string;
  created?: string;
  last_updated?: string;
};

export type ProductGroupCreateUpdate = {
  id?: number;
  subcategory_id?: number;
  name?: string;
  description?: string;
  sku_part?: string;
};

export type ProductOffering = {
  id: number;
  product_id: number;
  name: string;
  sku: string;
  sku_part: string;
  description: string;
  measure_type: string;
  measure_method: string;
  measure_unit: string;
  minimal_commit: number;
  maximum_commit: number;
  created: string;
  last_updated: string;
  has_sphere_content: boolean;
};
export type ProductOfferingCreateUpdate = {
  id?: number;
  product_id: number;
  name: string;
  sku_part: string;
  description: string;
  measure_type: string;
  measure_method: string;
  measure_unit: string;
  minimal_commit: number;
  maximum_commit: number;
  has_sphere_content: boolean;
};

export type Product = {
  id: number;
  product_group_id: number;
  product_type_id: number;
  name: string;
  offerings: ProductOffering[];
  description: string;
  managed: boolean;
  sku: string;
  sku_part: string;
  current_lifecycle_stage: string;
  current_lifecycle_start_date: string;
  current_lifecycle_end_date: string | null;
  region_ids: string[];
  created: string;
  last_updated: string;
};
export type ProductCreateUpdate = {
  id?: number;
  product_group_id: number;
  product_type_id: number;
  name: string;
  description: string;
  managed: boolean;
  sku_part: string;
  current_lifecycle_stage: string;
  current_lifecycle_start_date: string;
  current_lifecycle_end_date: string | null;
  region_ids: string[];
};

export type ProductSubCategory = {
  id: number;
  category_id: number;
  name: string;
  sku: string;
  sku_part: string;
  created: string;
  last_updated: string;
};

export type ProductSubCategoryCreateUpdate = {
  id?: number;
  category_id?: number;
  name?: string;
  sku_part?: string;
};

export type ProductType = {
  id: number;
  name: string;
  description: string;
};
export type ProductTypeCreateUpdate = {
  id?: number;
  name: string;
  description: string;
};

export type ServiceLevelObjective = {
  id: number;
  name: string;
  description: string;
  definition: string;
  measurement_type: string;
  measure_method: string;
  exceptions: string;
  customer_visibility: boolean;
  created: string;
  last_updated: string;
  penalties: ServiceLevelObjectivePenalty[];
};

export type ServiceLevelObjectiveCreateUpdate = {
  id?: number;
  name: string;
  description: string;
  definition: string;
  measurement_type: string;
  measure_method: string;
  exceptions: string;
  customer_visibility: boolean;
};

export type ComponentRelation = {
  id: number;
  description: string;
  name: string;
  type: string;
};

export type ComponentRelationCreateUpdate = {
  id?: number;
  description: string;
  name: string;
  type: string;
};

export type Responsibility = {
  id: number;
  name: string;
  description: string;
  actor: string;
};
export type ResponsibilityCreateUpdate = {
  id?: number;
  name: string;
  description: string;
  actor: string;
};

/**
 * Generic type that represents paginated data response from user management service.
 * @see {@link ekco/userman/domain/seedwork/schemas.py}
 */
export type GenericPagination<Type> = {
  items: Type;
  limit: number;
  offset: number;
  total: number;
};

/**
 * Paginated User Management Response Types
 */

export type PaginatedComponentType = GenericPagination<ComponentType[]>;
export type PaginatedCostPool = GenericPagination<CostPool[]>;
export type PaginatedFeature = GenericPagination<Feature[]>;
export type PaginatedLaborComponent = GenericPagination<LaborComponent[]>;
export type PaginatedServiceLevelObjectivePenalty = GenericPagination<
  ServiceLevelObjectivePenalty[]
>;
export type PaginatedProductCategory = GenericPagination<ProductCategory[]>;
export type PaginatedProductComponent = GenericPagination<ProductComponent[]>;
export type PaginatedProductGroup = GenericPagination<ProductGroup[]>;
export type PaginatedProductOffering = GenericPagination<ProductOffering[]>;
export type PaginatedProduct = GenericPagination<Product[]>;
export type PaginatedProductSubCategory = GenericPagination<ProductSubCategory[]>;
export type PaginatedProductType = GenericPagination<ProductType[]>;
export type PaginatedServiceLevelObjective = GenericPagination<ServiceLevelObjective[]>;

/**
 * Helper function to push data to paginated state
 * N.B: Use only w/ states that are rendered in tables, i.e w/ <PaginationComponent/>
 */
export const pushDataToPaginatedState = <T>(
  state: GenericPagination<T[]> | null,
  data: T,
): void => {
  // Push the object to state if on the last page.
  if (state !== null) {
    const remainingItemsCount = state.total % state.offset;
    if (remainingItemsCount >= state.limit || state.total === state.limit) {
      // Last page is full, the created item will be on the next page.
      state.total++;
    } else if (
      remainingItemsCount < state.limit ||
      isNaN(remainingItemsCount) // a % 0 === NaN
    ) {
      // Last page has spots, add to items
      state.items.push(data);
      state.total++;
      useLocalStorage().setItem('componentTypes', state);
    }
  }
};
